import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { type Location } from '@/models/account-management/Location';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import { type Playlist } from '@/models/media-network/Playlist';
import { type DayPart } from '@/models/account-management/DayPart';
import {
    type PlaylistSchedule, type SaveScheduleTarget,
    type WindowSource, windowSourceOf, PlaylistScheduleScope, RecurrenceFrequency,
    WEEKDAY_BITS, hasDay, toggleDay, minuteToTime, timeToMinute,
} from '@/models/media-network/PlaylistSchedule';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';

interface Props {
    open: boolean;
    brandId: string;
    /** Playlists this schedule can belong to — the Schedule tab spans all of them, so one is picked here. */
    playlists: Playlist[];
    schedule: PlaylistSchedule | null; // null = create
    locations: Location[];
    groups: LocationGroup[];
    dayParts: DayPart[];
    onClose: () => void;
}

// "Every N" and "day N of the month". Bounded sets, so they're pickers rather than number inputs.
const INTERVAL_OPTIONS = Array.from({ length: 99 }, (_, i) => i + 1);
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

const ScheduleDialog = ({ open, brandId, playlists, schedule, locations, groups, dayParts, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [playlistId, setPlaylistId] = useState('');
    const [enabled, setEnabled] = useState(true);

    const [startDate, setStartDate] = useState('');
    const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
    const [windowSource, setWindowSource] = useState<WindowSource>('times');
    const [startTime, setStartTime] = useState('07:00');
    const [endTime, setEndTime] = useState('11:00');
    const [dayPartId, setDayPartId] = useState('');
    const [frequency, setFrequency] = useState<RecurrenceFrequency>(RecurrenceFrequency.Daily);
    const [interval, setIntervalValue] = useState('1');
    const [daysOfWeek, setDaysOfWeek] = useState(0);
    const [dayOfMonth, setDayOfMonth] = useState('1');

    // The tier this schedule runs at. Targets are always all of this scope — see the note where it's picked.
    const [scope, setScope] = useState<PlaylistScheduleScope>(PlaylistScheduleScope.Brand);
    const [targets, setTargets] = useState<SaveScheduleTarget[]>([]);

    useEffect(() => {
        if (!open) return;

        // Editing keeps the schedule's own playlist: the API identifies a schedule by (playlistId,
        // scheduleId) and verifies the pairing, so a schedule cannot be moved between playlists here.
        setPlaylistId(schedule?.playlistId ?? playlists[0]?.id ?? '');
        setEnabled(schedule?.enabled ?? true);
        setStartDate(schedule?.startDate ?? '');
        setRecurrenceEndDate(schedule?.recurrenceEndDate ?? '');
        // An existing schedule with no day part and no times IS all day — not one missing its window.
        setWindowSource(schedule ? windowSourceOf(schedule) : 'times');
        setStartTime(minuteToTime(schedule?.startMinute) || '07:00');
        setEndTime(minuteToTime(schedule?.endMinute) || '11:00');
        setDayPartId(schedule?.dayPartId ?? '');
        setFrequency(schedule?.recurrenceFrequency ?? RecurrenceFrequency.Daily);
        setIntervalValue(String(schedule?.recurrenceInterval ?? 1));
        setDaysOfWeek(schedule?.recurrenceDaysOfWeek ?? 0);
        setDayOfMonth(String(schedule?.recurrenceDayOfMonth ?? 1));
        // Scope comes from what the schedule already targets. A pre-existing schedule could carry a mix of
        // tiers (the old UI allowed it), so the most specific one wins the picker and the rest are dropped on
        // the next save — which is the same answer the resolver would have given it anyway.
        const existing = schedule?.targets.map(t => ({ scope: t.scope, targetId: t.targetId })) ?? [];
        const tier = existing.length
            ? Math.max(...existing.map(t => t.scope)) as PlaylistScheduleScope
            : PlaylistScheduleScope.Brand;
        setScope(tier);
        // Brand scope has exactly one possible target, so it's implied by the choice rather than picked.
        setTargets(tier === PlaylistScheduleScope.Brand
            ? [{ scope: PlaylistScheduleScope.Brand, targetId: brandId }]
            : existing.filter(t => t.scope === tier));
    }, [open, schedule, playlists]);

    // Switching tier replaces the targets outright — ids from one tier mean nothing in another.
    const changeScope = (next: PlaylistScheduleScope) => {
        setScope(next);
        setTargets(next === PlaylistScheduleScope.Brand
            ? [{ scope: PlaylistScheduleScope.Brand, targetId: brandId }]
            : []);
    };

    const usingDayPart = windowSource === 'dayPart';
    const usingTimes = windowSource === 'times';

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            // Exactly one window shape goes on the wire; the other two are null. All Day sends none of the
            // three, which is what the backend reads as "the whole of every day this recurs".
            const body = {
                enabled,
                startDate,
                recurrenceEndDate: recurrenceEndDate || null,
                startMinute: usingTimes ? timeToMinute(startTime) : null,
                endMinute: usingTimes ? timeToMinute(endTime) : null,
                dayPartId: usingDayPart ? dayPartId : null,
                recurrenceFrequency: frequency,
                recurrenceInterval: Number(interval),
                recurrenceDaysOfWeek: frequency === RecurrenceFrequency.Weekly ? daysOfWeek : null,
                recurrenceDayOfMonth: frequency === RecurrenceFrequency.Monthly ? Number(dayOfMonth) : null,
                targets,
            };

            if (schedule) {
                await mediaNetworkApi.updateSchedule(playlistId, schedule.id, body);
            } else {
                await mediaNetworkApi.createSchedule(playlistId, body);
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistScheduleSaved });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.schedules(playlistId) });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.playlistScheduleSaveError }),
    });

    const savedLocations = locations.filter(l => !!l.id);
    const selectedLocationIds = targets.filter(t => t.scope === PlaylistScheduleScope.Location).map(t => t.targetId);
    const selectedGroupIds = targets.filter(t => t.scope === PlaylistScheduleScope.LocationGroup).map(t => t.targetId);

    // Only checked when the window IS explicit times. Equal times are ambiguous — zero-length, or all day? —
    // and rejected by the backend; all day now has its own option, so this stays an error rather than
    // quietly becoming one. Unequal either way round is fine: end < start simply wraps past midnight.
    const timesValid = !usingTimes || (
        timeToMinute(startTime) !== null && timeToMinute(endTime) !== null && startTime !== endTime
    );

    const canSave = !!playlistId
        && targets.length > 0
        && !!startDate
        && timesValid
        && (!usingDayPart || !!dayPartId)
        && (frequency !== RecurrenceFrequency.Weekly || daysOfWeek !== 0)
        && Number(interval) >= 1;

    const wraps = usingTimes && startTime > endTime;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{schedule ? strings.playlistEditSchedule : strings.playlistNewSchedule}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {/* Times are what a clock reads AT THE STORE, so a brand spanning timezones fires at the
                        same local hour in each one. Said out loud here because it's the single most
                        surprising thing about this screen. */}
                    <Alert severity="info" variant="outlined">{strings.playlistLocalTimeNotice}</Alert>

                    <FormControl fullWidth>
                        <InputLabel id="schedule-playlist-label">{strings.playlist}</InputLabel>
                        <Select
                            labelId="schedule-playlist-label"
                            label={strings.playlist}
                            value={playlistId}
                            // Locked when editing — see the note where this is seeded.
                            disabled={!!schedule}
                            onChange={(e) => setPlaylistId(e.target.value)}
                        >
                            {playlists.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* No schedule-type picker: every schedule recurs. What used to be "one time" is now
                        expressed as an All Day window on a recurrence. */}
                    {(
                        <>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label={strings.playlistStartDate}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    fullWidth
                                />
                                <TextField
                                    label={strings.playlistRecurrenceEnd}
                                    type="date"
                                    value={recurrenceEndDate}
                                    onChange={(e) => setRecurrenceEndDate(e.target.value)}
                                    helperText={strings.playlistRecurrenceEndHelp}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    fullWidth
                                />
                            </Stack>

                            <Divider />

                            <FormControl fullWidth>
                                <InputLabel id="window-source-label">{strings.playlistWindowSource}</InputLabel>
                                <Select
                                    labelId="window-source-label"
                                    label={strings.playlistWindowSource}
                                    value={windowSource}
                                    onChange={(e) => setWindowSource(e.target.value as WindowSource)}
                                >
                                    <MenuItem value="allDay">{strings.playlistWindowAllDay}</MenuItem>
                                    <MenuItem value="times">{strings.playlistWindowTimes}</MenuItem>
                                    <MenuItem value="dayPart" disabled={dayParts.length === 0}>
                                        {strings.playlistWindowDayPart}
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {/* All Day shows no time fields at all — that IS the window, not a window whose
                                inputs are hidden. */}
                            {usingDayPart ? (
                                <FormControl fullWidth>
                                    <InputLabel id="day-part-label">{strings.dayPart}</InputLabel>
                                    <Select
                                        labelId="day-part-label"
                                        label={strings.dayPart}
                                        value={dayPartId}
                                        onChange={(e) => setDayPartId(e.target.value)}
                                    >
                                        {dayParts.map((d) => (
                                            <MenuItem key={d.id} value={d.id}>
                                                {d.name} ({minuteToTime(d.startMinute)}–{minuteToTime(d.endMinute)})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {strings.playlistDayPartHelp}
                                    </Typography>
                                </FormControl>
                            ) : usingTimes ? (
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <TextField
                                        label={strings.playlistDailyStart}
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        fullWidth
                                    />
                                    <TextField
                                        label={strings.playlistDailyEnd}
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        error={startTime === endTime}
                                        // An end before the start is legal and means the window runs past
                                        // midnight — say so rather than letting it look like a mistake.
                                        helperText={
                                            startTime === endTime ? strings.playlistTimesMustDiffer
                                            : wraps ? strings.playlistWrapsMidnight
                                            : ' '
                                        }
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        fullWidth
                                    />
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    {strings.playlistWindowAllDayHelp}
                                </Typography>
                            )}

                            {/* Reads as a sentence: "Repeats every [2] [Weeks]". Dropdowns rather than number
                                inputs because both values are small, bounded sets — a spinner invites 0, -1
                                and 4000, each of which then has to be argued with. */}
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                    {strings.playlistRepeatsEvery}
                                </Typography>

                                <FormControl sx={{ minWidth: 90 }}>
                                    <Select
                                        value={interval}
                                        onChange={(e) => setIntervalValue(e.target.value)}
                                        inputProps={{ 'aria-label': strings.playlistEvery }}
                                    >
                                        {INTERVAL_OPTIONS.map((n) => (
                                            <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl sx={{ minWidth: 140 }}>
                                    <Select
                                        value={frequency}
                                        onChange={(e) => setFrequency(Number(e.target.value) as RecurrenceFrequency)}
                                        inputProps={{ 'aria-label': strings.playlistInterval }}
                                    >
                                        <MenuItem value={RecurrenceFrequency.Daily}>{strings.playlistDays}</MenuItem>
                                        <MenuItem value={RecurrenceFrequency.Weekly}>{strings.playlistWeeks}</MenuItem>
                                        <MenuItem value={RecurrenceFrequency.Monthly}>{strings.playlistMonths}</MenuItem>
                                    </Select>
                                </FormControl>

                                {frequency === RecurrenceFrequency.Monthly && (
                                    <FormControl sx={{ minWidth: 160 }}>
                                        <InputLabel id="day-of-month-label">{strings.playlistDayOfMonth}</InputLabel>
                                        <Select
                                            labelId="day-of-month-label"
                                            label={strings.playlistDayOfMonth}
                                            value={dayOfMonth}
                                            onChange={(e) => setDayOfMonth(e.target.value)}
                                        >
                                            {/* 31 is legal: it clamps to the last day in shorter months. */}
                                            {DAY_OF_MONTH_OPTIONS.map((n) => (
                                                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Stack>

                            {frequency === RecurrenceFrequency.Monthly && (
                                <Typography variant="caption" color="text.secondary">
                                    {strings.playlistDayOfMonthHelp}
                                </Typography>
                            )}

                            {frequency === RecurrenceFrequency.Weekly && (
                                <div>
                                    <Typography variant="caption" color="text.secondary">{strings.playlistOnDays}</Typography>
                                    <ToggleButtonGroup size="small" sx={{ display: 'flex', mt: 0.5 }}>
                                        {WEEKDAY_BITS.map((bit, i) => (
                                            <ToggleButton
                                                key={bit}
                                                value={bit}
                                                selected={hasDay(daysOfWeek, bit)}
                                                onChange={() => setDaysOfWeek(toggleDay(daysOfWeek, bit, !hasDay(daysOfWeek, bit)))}
                                                sx={{ flex: 1 }}
                                            >
                                                {strings.weekdayShort.split(',')[i]}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                    {daysOfWeek === 0 && (
                                        <Typography variant="caption" color="error">{strings.playlistPickADay}</Typography>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <Divider />

                    {/* One schedule, many targets — "run this at these 12 stores" is a single schedule here,
                        not twelve near-identical ones that then have to be edited in lockstep. */}
                    <Typography variant="subtitle2">{strings.playlistWhereItRuns}</Typography>

                    {/* One tier per schedule, chosen explicitly. The old shape — a brand checkbox sitting above
                        two always-visible pickers — let a schedule target the whole brand AND a single store at
                        once, which is just the brand target with extra steps.

                        Still MULTI-select within a tier: the target table exists so "run this at these twelve
                        stores" is one schedule rather than twelve near-identical ones. That's the deliberate
                        difference from Feedback, which is single-scope single-target. */}
                    <FormControl fullWidth>
                        <InputLabel id="schedule-scope-label">{strings.playlistAppliesTo}</InputLabel>
                        <Select
                            labelId="schedule-scope-label"
                            label={strings.playlistAppliesTo}
                            value={scope}
                            onChange={(e) => changeScope(Number(e.target.value) as PlaylistScheduleScope)}
                        >
                            <MenuItem value={PlaylistScheduleScope.Brand}>{strings.playlistScopeEntireBrand}</MenuItem>
                            <MenuItem value={PlaylistScheduleScope.LocationGroup}>{strings.playlistScopeLocationGroup}</MenuItem>
                            <MenuItem value={PlaylistScheduleScope.Location}>{strings.playlistScopeLocation}</MenuItem>
                        </Select>
                    </FormControl>

                    {scope === PlaylistScheduleScope.LocationGroup && (
                        <Autocomplete
                            multiple
                            options={groups.filter(g => !!g.id)}
                            getOptionLabel={(g) => g.name}
                            value={groups.filter(g => g.id && selectedGroupIds.includes(g.id))}
                            onChange={(_e, value) => setTargets(
                                value.map(g => ({ scope: PlaylistScheduleScope.LocationGroup, targetId: g.id! })),
                            )}
                            renderInput={(params) => <TextField {...params} label={strings.locationGroups} />}
                        />
                    )}

                    {/* Location.id is optional on the shared model (it doubles as the create shape), so
                        unsaved rows are filtered out rather than coerced. */}
                    {scope === PlaylistScheduleScope.Location && (
                        <Autocomplete
                            multiple
                            options={savedLocations}
                            getOptionLabel={(l) => l.name}
                            value={savedLocations.filter(l => selectedLocationIds.includes(l.id!))}
                            onChange={(_e, value) => setTargets(
                                value.map(l => ({ scope: PlaylistScheduleScope.Location, targetId: l.id! })),
                            )}
                            renderInput={(params) => <TextField {...params} label={strings.locations} />}
                        />
                    )}

                    {targets.length === 0 && (
                        <Typography variant="caption" color="error">{strings.playlistTargetRequired}</Typography>
                    )}

                    {/* Precedence is about competing SCHEDULES, not this one's targets — worth saying wherever
                        a schedule is narrower than the brand, since that's exactly when it overrides one. */}
                    {scope !== PlaylistScheduleScope.Brand && (
                        <Alert severity="info" variant="outlined">{strings.playlistPrecedenceNotice}</Alert>
                    )}

                    <FormControlLabel
                        control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                        label={strings.enabled}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{strings.cancel}</Button>
                <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>
                    {strings.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDialog;
